import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import OfflineBanner from "./components/OfflineBanner";
import InstallPrompt from "./components/InstallPrompt";
import GuestBanner from "./components/GuestBanner";
import UpgradeBanner from "./components/UpgradeBanner";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { ToastProvider, toast } from "./components/ui/Toast";
import { LoadingSpinner } from "./components/ui/LoadingSpinner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Onboarding from "./components/Onboarding";
import ErrorBoundaryWithReset from "./components/ErrorBoundaryWithReset";
import ScrollToTop from "./components/ScrollToTop";
import Breadcrumbs from "./components/Breadcrumbs";
import { clearApiCache } from "./utils/sw";
import { ThemeProvider } from "./contexts/ThemeContext";
import { initGoogleAnalytics, trackPageView } from "./services/googleAnalytics";

const About = lazy(() => import("./pages/About"));
const BillingCancel = lazy(() => import("./pages/BillingCancel"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const Contact = lazy(() => import("./pages/Contact"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const MealPlans = lazy(() => import("./pages/MealPlans"));
const MealPlanTemplates = lazy(() => import("./pages/MealPlanTemplates"));
const Meals = lazy(() => import("./pages/Meals"));
const OfflineFallback = lazy(() => import("./pages/OfflineFallback"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PublicCollections = lazy(() => import("./pages/PublicCollections"));
const Register = lazy(() => import("./pages/Register"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Settings = lazy(() => import("./pages/Settings"));
const SharedMeal = lazy(() => import("./pages/SharedMeal"));
const SharedMealPlan = lazy(() => import("./pages/SharedMealPlan"));
const SharedCollection = lazy(() => import("./pages/SharedCollection"));
const SharedMealPlanTemplate = lazy(
  () => import("./pages/SharedMealPlanTemplate"),
);
const ShoppingLists = lazy(() => import("./pages/ShoppingLists"));
const Terms = lazy(() => import("./pages/Terms"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));

// AppContent component to access router hooks
const AppContent: React.FC = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const location = useLocation();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { isOnline, wasOffline } = useOnlineStatus();

  // Initialise GA4 once at startup
  useEffect(() => {
    initGoogleAnalytics();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Show toast when connection is restored after being offline
  useEffect(() => {
    if (wasOffline) {
      toast.success("Connection restored", "You\u0026apos;re back online.");
    }
  }, [wasOffline]);

  // Check if user needs onboarding
  useEffect(() => {
    if (isAuthenticated && user && !user.onboarding_completed) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [isAuthenticated, user]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (user) {
      const updatedUser = { ...user, onboarding_completed: true };
      updateUser(updatedUser);
    }
  };

  return (
    <>
      <OfflineBanner isOnline={isOnline} wasOffline={wasOffline} />
      <InstallPrompt />
      <ToastProvider position="top-right" />
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Header />
      <GuestBanner />
      <UpgradeBanner />
      <Breadcrumbs />
      <main id="main-content" className="min-h-screen bg-secondary-50">
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              <LoadingSpinner size="lg" text="Loading..." />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/offline" element={<OfflineFallback />} />
            <Route path="/public-collections" element={<PublicCollections />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/shared-meals/:token" element={<SharedMeal />} />
            <Route
              path="/shared-meal-plans/:token"
              element={<SharedMealPlan />}
            />
            <Route
              path="/shared-collections/:token"
              element={<SharedCollection />}
            />
            <Route
              path="/shared-meal-plan-templates/:token"
              element={<SharedMealPlanTemplate />}
            />
            <Route element={<ProtectedRoute />}>
              <Route path="/meals" element={<Meals />} />
              <Route path="/meal-plans" element={<MealPlans />} />
              <Route
                path="/meal-plan-templates"
                element={<MealPlanTemplates />}
              />
              <Route path="/shopping-lists" element={<ShoppingLists />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
      <Footer />

      {showOnboarding && user && (
        <Onboarding user={user} onComplete={handleOnboardingComplete} />
      )}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <ScrollToTop />
            <ErrorBoundaryWithReset>
              <AppContent />
            </ErrorBoundaryWithReset>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
